import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_9d2782f9-5276-4e92-beba-9f63a63d9a7f/artifacts/h7wg6aqw_file_0000000000947230a410da9a77c2c8e9.png",
  massage: "https://customer-assets.emergentagent.com/job_9d2782f9-5276-4e92-beba-9f63a63d9a7f/artifacts/ycus9ouu_file_000000001ef471f8805d3e317caa5f32.png",
};

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

// Treatment descriptions
const treatments = [
  {
    id: "ultimate",
    name: "Ultimate Healing Massage",
    emoji: "✨",
    description: `The Ultimate Healing Massage is a deeply immersive, full-body healing experience designed to restore balance to your body, mind, and energy.

We begin with a gentle consultation, creating a safe and calming space for you to share how you've been feeling — physically, emotionally, or energetically. This allows the session to be fully tailored to your needs.

You'll then receive a personalised massage, flowing intuitively between soft, relaxing touch and deeper pressure, depending on what your body is calling for.

As your body begins to fully unwind, the treatment moves into deeper healing. A vibroacoustic Tibetan bowl is placed on the body, allowing soothing vibrations to travel through you, supporting cellular regeneration and calming the nervous system.

The session is then enhanced with Reiki energy healing to rebalance and clear your energy, followed by the gentle sounds of crystal singing bowls, creating a deeply restorative sound bath experience.

This is more than a massage — it is a full mind, body, and soul reset.`,
    pricing: [
      { duration: "90 minutes", price: "£100" },
      { duration: "2 hours", price: "£140" },
    ],
    category: "massage",
    filter: "Ultimate Healing Massage",
  },
  {
    id: "energy-reset",
    name: "Energy Reset",
    emoji: "🌿",
    description: `The Energy Reset is a grounding and restorative treatment designed to bring your body and energy back into balance.

Each session begins with a short consultation to understand how you're feeling and what your body needs.

You'll receive a tailored massage, adapted to your preference — whether that's gentle and relaxing or deeper, more focused pressure.

The treatment also includes vibroacoustic healing using a Tibetan bowl placed on the body, allowing calming vibrations to move through you and support nervous system regulation.

Reiki energy healing is then used to help rebalance your energy and promote a sense of calm, clarity, and wellbeing.

This session is perfect for those looking for a powerful reset in a shorter, more focused treatment.`,
    pricing: [
      { duration: "60 minutes", price: "£80" },
      { duration: "90 minutes", price: "£100" },
      { duration: "2 hours", price: "£130" },
    ],
    category: "massage",
    filter: "Energy Reset",
  },
  {
    id: "full-body",
    name: "Tailored Full Body Massage",
    emoji: "🌿",
    description: `A deeply relaxing and restorative full body massage, completely tailored to your individual needs.

Each session is designed around your preference — whether you're looking for a soft, calming experience, deeper pressure to release tension, or a balanced combination of both. The treatment flows intuitively, allowing your body to fully unwind and let go.

This is your time to switch off, reset, and reconnect with your body in a peaceful, nurturing space.`,
    pricing: [
      { duration: "60 minutes", price: "£70" },
      { duration: "90 minutes", price: "£100" },
      { duration: "2 hours", price: "£140" },
    ],
    category: "massage",
    filter: "Tailored Full Body Massage",
  },
  {
    id: "reiki",
    name: "Reiki Healing",
    emoji: "✨",
    description: `A gentle yet powerful energy healing treatment designed to restore balance within the body and mind.

Reiki works by channeling energy through the body, helping to clear blockages, reduce stress, and support emotional and energetic healing. During the session, you'll remain fully clothed, lying comfortably as energy flows where it is most needed.

This treatment is deeply calming and can leave you feeling lighter, clearer, and more aligned.`,
    pricing: [{ duration: "60 minutes", price: "£60" }],
    category: "sound_healing",
    filter: "Reiki",
  },
  {
    id: "vibroacoustic",
    name: "Vibroacoustic Sound Healing",
    emoji: "🔔",
    description: `A unique and deeply relaxing treatment using a Tibetan bowl placed directly on the body.

As the bowl is gently played, vibrations travel through your body, working on a cellular level to release tension, calm the nervous system, and promote deep relaxation.

This treatment is perfect for those feeling stressed, overwhelmed, or disconnected, offering a powerful reset through the healing power of sound and vibration.`,
    pricing: [{ duration: "60 minutes", price: "£65" }],
    category: "sound_healing",
    filter: "Vibroacoustic",
  },
];

export default function SoundHealingPage() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        const filtered = response.data.filter(
          (s) => s.category === "massage" || s.category === "sound_healing"
        );
        setServices(filtered);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, []);

  const getServiceByName = (filterText) => {
    return services.find((s) => s.name.includes(filterText));
  };

  return (
    <div data-testid="sound-healing-page">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-end" data-testid="sound-healing-hero">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.hero}
            alt="Treatment room"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        </div>

        <div className="container-custom relative z-10 pb-24 pt-40">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
              ✨ Treatments
            </p>
            <h1 className="heading-xl mb-6 text-foreground">
              Deep Relaxation,<br />
              <em className="italic">Healing & Reconnection</em>
            </h1>
            <p className="body-text text-muted-foreground max-w-lg">
              Step into a space of deep relaxation, healing, and reconnection. 
              Each treatment is designed to support your body, calm your mind, 
              and restore your energy — leaving you feeling balanced, grounded, and renewed.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Price List */}
      <section className="section-spacing bg-muted" data-testid="price-list-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
              At a Glance
            </p>
            <h2 className="heading-lg">
              Treatment <em className="italic">Menu</em>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-3xl mx-auto"
          >
            {treatments.map((treatment, tIndex) => (
              <div key={treatment.id} className={tIndex !== treatments.length - 1 ? "mb-8" : ""}>
                <h3 className="font-serif text-xl text-foreground mb-4">{treatment.name}</h3>
                <div className="space-y-0">
                  {treatment.pricing.map((p, i) => (
                    <div
                      key={`pl-${treatment.id}-${i}`}
                      className="flex items-center justify-between py-3 border-b border-border last:border-b-0"
                    >
                      <div className="flex items-center gap-3">
                        <Clock className="w-4 h-4 text-primary" />
                        <span className="text-sm text-muted-foreground">{p.duration}</span>
                      </div>
                      <div className="flex-1 mx-4 border-b border-dotted border-border/60" />
                      <span className="font-serif text-lg text-foreground">{p.price}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <p className="text-xs text-muted-foreground text-center mt-8">
              £20 deposit secures your booking. Remaining balance payable on arrival.
            </p>

            <div className="text-center mt-8">
              <Link to="/booking">
                <Button
                  className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground px-10 py-6 text-xs tracking-[0.2em] uppercase btn-animate"
                  data-testid="price-list-book-btn"
                >
                  Book a Treatment
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Treatments */}
      <section className="section-spacing" data-testid="treatments-section">
        <div className="container-custom">
          {treatments.map((treatment, index) => (
            <motion.div
              key={treatment.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className={`py-16 ${index !== treatments.length - 1 ? "border-b border-border" : ""}`}
              data-testid={`treatment-${treatment.id}`}
            >
              <div className="max-w-3xl">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl">{treatment.emoji}</span>
                  <h2 className="heading-md text-foreground">{treatment.name}</h2>
                </div>
                
                <div className="body-text text-muted-foreground whitespace-pre-line mb-8">
                  {treatment.description}
                </div>

                {/* Pricing */}
                <div className="mb-8">
                  <p className="text-sm tracking-[0.2em] uppercase text-primary mb-4">
                    💛 Pricing
                  </p>
                  <div className="flex flex-wrap gap-4">
                    {treatment.pricing.map((price, i) => (
                      <div
                        key={`tp-${price.duration}`}
                        className="bg-muted px-6 py-4 border border-border"
                      >
                        <span className="font-serif text-lg text-foreground">
                          {price.duration}
                        </span>
                        <span className="text-muted-foreground mx-2">—</span>
                        <span className="font-serif text-xl text-primary">
                          {price.price}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Book Button */}
                <Link to={`/booking`}>
                  <Button
                    className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-xs tracking-[0.15em] uppercase btn-animate"
                    data-testid={`book-${treatment.id}-btn`}
                  >
                    Book {treatment.name}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* What to Expect */}
      <section className="section-spacing bg-[#1a1a1a]" data-testid="treatments-what-to-expect">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="mb-12">
                <p className="text-xs tracking-[0.3em] uppercase text-[#C9A96E] mb-4">
                  Your Experience
                </p>
                <h2 className="font-serif text-4xl sm:text-5xl font-light text-white">
                  What to <em className="italic text-[#C9A96E]">Expect</em>
                </h2>
              </div>

              <div className="space-y-8">
                <div className="flex gap-6">
                  <div className="w-8 h-8 rounded-full bg-[#C9A96E]/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-xs font-medium text-[#C9A96E]">1</span>
                  </div>
                  <div>
                    <h4 className="font-serif text-lg text-white mb-2">Arrival & Welcome</h4>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      When you arrive, you'll be welcomed into a calm, peaceful space. Take a moment to breathe, settle in, and leave the outside world behind.
                    </p>
                  </div>
                </div>

                <div className="flex gap-6">
                  <div className="w-8 h-8 rounded-full bg-[#C9A96E]/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-xs font-medium text-[#C9A96E]">2</span>
                  </div>
                  <div>
                    <h4 className="font-serif text-lg text-white mb-2">Consultation</h4>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      We'll have a gentle chat about how you're feeling — physically, emotionally, and energetically. This helps me tailor the session entirely to your needs.
                    </p>
                  </div>
                </div>

                <div className="flex gap-6">
                  <div className="w-8 h-8 rounded-full bg-[#C9A96E]/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-xs font-medium text-[#C9A96E]">3</span>
                  </div>
                  <div>
                    <h4 className="font-serif text-lg text-white mb-2">Your Treatment</h4>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      You'll be guided onto the treatment table and given privacy to get comfortable. The session flows intuitively — combining massage, sound healing, energy work, or vibroacoustic therapy depending on your chosen treatment.
                    </p>
                  </div>
                </div>

                <div className="flex gap-6">
                  <div className="w-8 h-8 rounded-full bg-[#C9A96E]/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-xs font-medium text-[#C9A96E]">4</span>
                  </div>
                  <div>
                    <h4 className="font-serif text-lg text-white mb-2">Aftercare</h4>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      After your session, take a few moments to gently come back. There's no rush. I'll share any insights from the treatment and offer guidance on how to support your body and energy in the days that follow.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:block"
            >
              <div className="aspect-[3/4] overflow-hidden sticky top-32">
                <img
                  src={IMAGES.massage}
                  alt="Sacred Sounds Studio treatment space"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="section-spacing bg-muted" data-testid="closing-section">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl mx-auto"
          >
            <p className="text-2xl mb-6">💫</p>
            <p className="body-text text-muted-foreground text-lg italic leading-relaxed">
              Each treatment is created with intention, care, and intuition. 
              Whether you are seeking relaxation, emotional release, or energetic balance, 
              you will be held in a safe and nurturing space throughout your experience.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-primary" data-testid="sound-healing-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="heading-lg mb-6 text-primary-foreground">
              Ready to <em className="italic">Heal?</em>
            </h2>
            <p className="body-text text-primary-foreground/80 mb-10">
              Book your treatment today and step into a space of deep relaxation and restoration.
            </p>
            <Link to="/booking">
              <Button
                size="lg"
                className="rounded-none bg-background hover:bg-background/90 text-foreground px-12 py-7 text-xs tracking-[0.2em] uppercase btn-animate"
                data-testid="book-session-cta-btn"
              >
                Book Your Treatment
                <ArrowRight className="ml-3 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
